import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, ReactNode } from "react";
import { Snackbar, Alert } from "@mui/material";

interface SnackbarContextType {
  showSnackbar: (message: string, severity?: "success" | "error" | "warning" | "info") => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

// Référence globale utilisée par l'intercepteur axios (hors React)
let _globalShowSnackbar: SnackbarContextType['showSnackbar'] | null = null;
export const showGlobalSnackbar: SnackbarContextType['showSnackbar'] = (msg, sev) => {
  _globalShowSnackbar?.(msg, sev);
};

export const SnackbarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState<"success" | "error" | "warning" | "info">("info");

  // Référence STABLE — sinon chaque render du provider crée une nouvelle
  // fonction → consumers `useEffect([..., showSnackbar])` ré-exécutent en
  // boucle, ce qui empêche de fermer un snackbar qui se ré-affiche aussitôt.
  const showSnackbar = useCallback((msg: string, sev: "success" | "error" | "warning" | "info" = "info") => {
    setMessage(msg);
    setSeverity(sev);
    setOpen(true);
  }, []);

  // Enregistrer la référence globale dès que le provider est monté
  useEffect(() => {
    _globalShowSnackbar = showSnackbar;
    return () => { _globalShowSnackbar = null; };
  }, [showSnackbar]);

  // value mémoïsée — une seule identité tant que showSnackbar ne change pas.
  const contextValue = useMemo(() => ({ showSnackbar }), [showSnackbar]);

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <Snackbar open={open} autoHideDuration={3000} onClose={() => setOpen(false)} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={() => setOpen(false)} severity={severity} sx={{ width: "100%" }}>
          {message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
};
